import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  HostBinding,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type ButtonVariant  = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize     = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
export type ButtonColor    = 'amarante' | 'moonstone' | 'rose' | 'mint' | 'semantic-red' | 'semantic-yellow' | 'semantic-green';
export type ButtonState    = 'default' | 'hovered' | 'pressed' | 'disabled';
export type ButtonHtmlType = 'button' | 'submit' | 'reset';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {

  // ── Apparence ──────────────────────────────────────────────────────────────

  /** Variante visuelle : 'primary' | 'secondary' | 'tertiary' */
  @Input() variant: ButtonVariant = 'primary';

  /** Taille : 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' */
  @Input() size: ButtonSize = 'medium';

  /** Couleur du thème */
  @Input() color: ButtonColor = 'amarante';

  /**
   * Forcer un état visuel (utile pour Storybook / tests).
   * En production, les états hover/pressed/disabled sont gérés nativement par CSS.
   * 'disabled' ici positionne aussi l'attribut HTML disabled.
   */
  @Input() state: ButtonState = 'default';

  // ── Contenu ────────────────────────────────────────────────────────────────

  /** Texte du bouton. Si vide et addLabel=true, le slot label est rendu vide. */
  @Input() label = '';

  /** Afficher le label (masquer pour un bouton icon-only) */
  @Input() addLabel = true;

  /** Afficher l'icône à gauche (le contenu est fourni via slot="icon-left") */
  @Input() addIconLeft = false;

  /** Afficher l'icône à droite (le contenu est fourni via slot="icon-right") */
  @Input() addIconRight = false;

  // ── Comportement HTML ──────────────────────────────────────────────────────

  /** Type HTML natif du <button> */
  @Input() htmlType: ButtonHtmlType = 'button';

  /** Largeur 100% du parent */
  @Input() fullWidth = false;

  // ── Accessibilité ──────────────────────────────────────────────────────────

  /** Nom accessible du <button> (aria-label), indispensable pour un bouton icon-only. */
  readonly ariaLabel = input<string | null>(null);

  /** État d'un bouton bascule (aria-pressed du <button>) ; null pour un bouton simple. */
  readonly ariaPressed = input<boolean | null>(null);

  // ── Event ──────────────────────────────────────────────────────────────────

  @Output() clicked = new EventEmitter<MouseEvent>();

  // ── Dérivés ────────────────────────────────────────────────────────────────

  @HostBinding('style.display')
  get hostDisplay(): string { return this.fullWidth ? 'flex' : 'inline-flex'; }

  @HostBinding('style.width')
  get hostWidth(): string { return this.fullWidth ? '100%' : ''; }

  get isDisabled(): boolean { return this.state === 'disabled'; }

  /** Classes BEM dynamiques passées au <button> */
  get btnClasses(): Record<string, boolean> {
    return {
      'btn': true,
      [`btn--${this.variant}`]: true,
      [`btn--${this.size}`]: true,
      [`btn--${this.color}`]: true,
      [`btn--state-${this.state}`]: this.state !== 'default',
      'btn--icon-only': !this.addLabel || !this.label,
      'btn--full-width': this.fullWidth,
    };
  }

  onClick(event: MouseEvent): void {
    if (this.isDisabled) return;
    this.clicked.emit(event);
  }
}